import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Spinner,
  Stat,
  StatArrow,
  StatGroup,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
} from '@chakra-ui/react';
import moment from 'moment';
import ReactDatePicker from 'react-datepicker';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';

import '../../../components/DatePicker/styles.css';
import { GET_USER_PARTNER_DISPOSSESSION_STATISTICS, InternalUserPartnerDispossessionStatisticsQuery } from './PartnerStatistics.query';
import { defaultStatisticsRange, isDefaultStatisticsRange, toStatisticsPeriod } from './partnerStatisticsRange';

const CHART_COLORS = {
  ups: '#4A808C',
  amazon: '#78B3BA',
  grid: '#1e293b',
  text: '#94a3b8',
};

const monthChangePercent = (thisMonth: number, lastMonth: number) => {
  if (!thisMonth) return 0;
  return Math.round(((thisMonth - lastMonth) / (lastMonth || 1)) * 100);
};

const TabPanelFrame = ({ children }: { children: React.ReactNode }) => (
  <Box borderLeftWidth="1px" borderRightWidth="1px" borderBottomWidth="1px" px="4" py="8">
    {children}
  </Box>
);

const ChartTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; name: string; value: number }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;

  return (
    <Box bg="gray.800" borderWidth="1px" borderColor="gray.600" px="3" py="2">
      <Text fontSize="sm">{label}</Text>
      {payload.map((entry) => (
        <Text key={entry.dataKey} fontSize="sm">
          {entry.name}: {entry.value}
        </Text>
      ))}
    </Box>
  );
};

const ChartLegend = ({ payload }: { payload?: Array<{ value: string; color: string }> }) => (
  <Flex justify="center" align="center" gap="4" mt="2">
    {payload?.map((entry) => (
      <Flex key={entry.value} align="center" gap="2">
        <Box w="10px" h="10px" bg={entry.color} />
        <Text fontSize="sm">{entry.value}</Text>
      </Flex>
    ))}
  </Flex>
);

type PartnerStatisticsProps = {
  userId?: string;
};

const MonthField = ({
  label,
  selected,
  minDate,
  maxDate,
  onChange,
}: {
  label: string;
  selected: Date;
  minDate?: Date;
  maxDate?: Date;
  onChange: (date: Date) => void;
}) => (
  <FormControl w="180px">
    <FormLabel fontSize="sm" mb="1">
      {label}
    </FormLabel>
    <ReactDatePicker
      selected={selected}
      onChange={(date) => {
        if (date) onChange(moment(date).startOf('month').toDate());
      }}
      minDate={minDate}
      maxDate={maxDate}
      showMonthYearPicker
      showFullMonthYearPicker
      dateFormat="MM.yyyy"
      placeholderText="MM.JJJJ"
      showPopperArrow={false}
      popperPlacement="bottom-start"
    />
  </FormControl>
);

export const PartnerStatistics = ({ userId }: PartnerStatisticsProps) => {
  const initialRange = defaultStatisticsRange();
  const [startMonth, setStartMonth] = useState(initialRange.start);
  const [endMonth, setEndMonth] = useState(initialRange.end);
  const currentMonth = moment().endOf('month').toDate();

  const { data, loading, error } = useQuery<InternalUserPartnerDispossessionStatisticsQuery>(GET_USER_PARTNER_DISPOSSESSION_STATISTICS, {
    variables: {
      userId,
      startPeriod: toStatisticsPeriod(startMonth),
      endPeriod: toStatisticsPeriod(endMonth),
    },
    skip: !userId,
  });

  const statistics = data?.internalUserPartnerDispossessionStatistics;
  const upsThisMonth = statistics?.upsThisMonth ?? 0;
  const upsLastMonth = statistics?.upsLastMonth ?? 0;
  const amazonThisMonth = statistics?.amazonThisMonth ?? 0;
  const amazonLastMonth = statistics?.amazonLastMonth ?? 0;
  const chartData = (statistics?.months ?? []).map((month) => ({
    label: moment(month.period, 'YYYY-MM').format('MMM YYYY'),
    UPS: month.upsCount,
    Amazon: month.amazonCount,
  }));

  const resetRange = () => {
    const range = defaultStatisticsRange();
    setStartMonth(range.start);
    setEndMonth(range.end);
  };

  return (
    <TabPanelFrame>
      <Flex mb="8" gap="4" align="flex-end" wrap="wrap">
        <MonthField
          label="Von"
          selected={startMonth}
          maxDate={moment.min(moment(endMonth), moment(currentMonth)).toDate()}
          onChange={(date) => {
            setStartMonth(date);
            if (moment(date).isAfter(endMonth, 'month')) setEndMonth(date);
          }}
        />
        <MonthField
          label="Bis"
          selected={endMonth}
          minDate={startMonth}
          maxDate={currentMonth}
          onChange={(date) => {
            setEndMonth(date);
            if (moment(date).isBefore(startMonth, 'month')) setStartMonth(date);
          }}
        />
        <Button size="sm" fontWeight="medium" onClick={resetRange} isDisabled={isDefaultStatisticsRange(startMonth, endMonth)}>
          Letzte 12 Monate
        </Button>
      </Flex>
      {loading ? (
        <Flex width="100%" height="300px" justifyContent="center" alignItems="center">
          <Spinner />
        </Flex>
      ) : error ? (
        <Flex width="100%" height="300px" justifyContent="center" alignItems="center">
          <Text>Ein Fehler ist aufgetreten. Schreib Osamah an.</Text>
        </Flex>
      ) : (
        <>
          <StatGroup mb="8">
            <Stat>
              <StatLabel>UPS {moment().format('MMM YYYY')}</StatLabel>
              <StatNumber>{upsThisMonth}</StatNumber>
              <StatHelpText>
                <StatArrow type={upsThisMonth >= upsLastMonth ? 'increase' : 'decrease'} />
                {monthChangePercent(upsThisMonth, upsLastMonth)}%
              </StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>UPS {moment().subtract(1, 'month').format('MMM YYYY')}</StatLabel>
              <StatNumber>{upsLastMonth}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Amazon {moment().format('MMM YYYY')}</StatLabel>
              <StatNumber>{amazonThisMonth}</StatNumber>
              <StatHelpText>
                <StatArrow type={amazonThisMonth >= amazonLastMonth ? 'increase' : 'decrease'} />
                {monthChangePercent(amazonThisMonth, amazonLastMonth)}%
              </StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Amazon {moment().subtract(1, 'month').format('MMM YYYY')}</StatLabel>
              <StatNumber>{amazonLastMonth}</StatNumber>
            </Stat>
          </StatGroup>
          <Box height="360px" overflowX="auto">
            <Box height="100%" minW={`${Math.max(720, chartData.length * 56)}px`}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                  <XAxis dataKey="label" fontSize={10} stroke={CHART_COLORS.text} interval={0} angle={-40} textAnchor="end" height={64} />
                  <YAxis allowDecimals={false} fontSize={10} stroke={CHART_COLORS.text} />
                  <RechartsTooltip content={<ChartTooltip />} />
                  <Legend content={<ChartLegend />} />
                  <Bar dataKey="UPS" fill={CHART_COLORS.ups} />
                  <Bar dataKey="Amazon" fill={CHART_COLORS.amazon} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </>
      )}
    </TabPanelFrame>
  );
};

export default PartnerStatistics;
